/**
 * Studio login — Document 6 §9.1.
 *
 * The only screen in the product where the product's own name appears: the
 * studio is unknown until credentials are checked, and this screen is never
 * seen by a student.
 */

import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { Button } from '../components/ui/Button.js';
import { Field } from '../components/ui/Field.js';
import { useSession } from '../session/SessionContext.js';
import './Login.css';

export function Login() {
  const { status, refresh } = useSession();
  const navigate = useNavigate();

  const [studioCode, setStudioCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  /*
   * Bounces a visitor who is already signed in and opens this page by hand.
   * Not one who has just signed in here: `refresh()` flips the status while
   * this component is still mounted, and sending them to "/" would overrule
   * the destination the login response chose — which is how an owner ended up
   * on the dashboard instead of being asked whose profile to use.
   */
  if (status === 'authenticated' && !submitted) return <Navigate to="/" replace />;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setSubmitted(true);
    setError(undefined);

    try {
      const result = await api.post<{
        role: 'owner' | 'instructor';
        requiresInstructorSelection: boolean;
      }>('/api/auth/login', { studioCode, username, password });

      // The session has to be loaded before the next screen mounts, or it
      // sees an anonymous one and bounces straight back here.
      await refresh();

      if (result.requiresInstructorSelection) {
        navigate('/egitmen-secimi', { replace: true });
      } else navigate('/', { replace: true });
    } catch (err) {
      // The message never distinguishes an unknown username from a wrong
      // password, so login cannot be used to discover which studios exist.
      setError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="login__brand-name">Trackpil</span>
          <span className="login__brand-tagline">Stüdyo Girişi</span>
        </div>

        <form onSubmit={submit} className="login__form" noValidate>
          {/* Three fields for everyone: the studio, then who you are inside
              it. The owner's username is the one their welcome mail names. */}
          <Field
            label="Salon kodu"
            autoComplete="organization"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={studioCode}
            onChange={(e) => setStudioCode(e.target.value)}
            required
          />

          <Field
            label="Kullanıcı adı"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />

          <Field
            label="Şifre"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={error}
            required
          />

          <Button type="submit" loading={busy}>
            {busy ? 'Giriş yapılıyor…' : 'Giriş Yap'}
          </Button>
        </form>

        <p className="login__help">
          Şifrenizi unuttuysanız stüdyo yöneticinizle iletişime geçin.
        </p>
      </div>
    </main>
  );
}
